import { BaseModel } from '../../../shared/src/lib/shared/models/entity/base/base.model';

// -----------------------------------------------------------------------------------------------------
// @ TYPES
// -----------------------------------------------------------------------------------------------------

/**
 * The signature every entity fixture factory follows.
 * The index drives the deterministic id and the rotation through the sample value pools.
 */
export type FixtureFactory<TModel> = (overrides?: Partial<TModel>, index?: number) => TModel;

// -----------------------------------------------------------------------------------------------------
// @ CONSTANTS
// -----------------------------------------------------------------------------------------------------

/** The principal all fixture entities are created and modified by. */
export const FIXTURE_PRINCIPAL_ID: string = 'f1x7u2e0-0000-4000-8000-000000000001';

export const FIXTURE_PRINCIPAL_NAME: string = 'Seed User';

/** Fixed clock so fixtures never drift between runs (and snapshots stay stable). */
export const FIXTURE_NOW: Date = new Date('2026-01-15T09:00:00.000Z');

// -----------------------------------------------------------------------------------------------------
// @ IDS
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a deterministic, guid-shaped id for a fixture entity.
 * The same namespace and index always produce the same id, so relations can be wired
 * across fixture files without passing objects around.
 *
 * @param namespace - The entity namespace, e.g. 'property'.
 * @param index - The zero-based index of the entity within its namespace.
 * @returns {string} A stable guid-shaped id.
 */
export function fixtureId(namespace: string, index: number): string {
    const prefix = hashCode(namespace).toString(16).padStart(8, '0').slice(0, 8);
    const suffix = index.toString(16).padStart(12, '0');

    return `${prefix}-0000-4000-8000-${suffix}`;
}

// -----------------------------------------------------------------------------------------------------
// @ AUDIT
// -----------------------------------------------------------------------------------------------------

/**
 * Builds the BaseModel audit fields for a fixture entity.
 * Timestamps are derived from the index so every entity has a slightly different history.
 *
 * @param index - The zero-based index of the entity, used to offset the timestamps.
 * @returns {Partial<BaseModel>} The audit fields, without the soft-delete markers.
 */
export function auditFields(index: number = 0): Partial<BaseModel> {
    return {
        createdAt: isoDaysAgo(180 - (index % 90)),
        createdBy: FIXTURE_PRINCIPAL_ID,
        createdByName: FIXTURE_PRINCIPAL_NAME,
        createdByPrincipalType: 'User',
        modifiedAt: isoDaysAgo(30 - (index % 20)),
        modifiedBy: FIXTURE_PRINCIPAL_ID,
        modifiedByPrincipalType: 'User',
    };
}

/**
 * Builds the soft-delete markers, for fixtures that need a deleted entity.
 *
 * @param daysAgo - How many days ago the entity was deleted.
 * @returns {Partial<BaseModel>} The soft-delete fields.
 */
export function deletedFields(daysAgo: number = 3): Partial<BaseModel> {
    return {
        deletedAt: isoDaysAgo(daysAgo),
        deletedBy: FIXTURE_PRINCIPAL_ID,
        deletedByPrincipalType: 'User',
    };
}

/**
 * Returns an ISO date-time string relative to the fixed fixture clock.
 *
 * @param days - How many days before FIXTURE_NOW.
 * @returns {string} An ISO date-time string.
 */
export function isoDaysAgo(days: number): string {
    const date = new Date(FIXTURE_NOW);

    date.setDate(date.getDate() - days);

    return date.toISOString();
}

// -----------------------------------------------------------------------------------------------------
// @ COLLECTIONS
// -----------------------------------------------------------------------------------------------------

/**
 * Picks a value from a sample pool by index, wrapping around when the index exceeds the pool.
 *
 * @param values - The sample pool.
 * @param index - The index to pick.
 * @returns {T} The picked value.
 */
export function cycle<T>(values: readonly T[], index: number): T {
    return values[index % values.length];
}

/**
 * Builds a list of entities by invoking the factory once per index.
 *
 * @param count - How many entities to build.
 * @param factory - The factory, receiving the zero-based index.
 * @returns {T[]} The built entities.
 */
export function range<T>(count: number, factory: (index: number) => T): T[] {
    return Array.from({ length: count }, (_, index: number) => factory(index));
}

/**
 * Builds a list of entities from a fixture factory, with optional per-index overrides.
 *
 * @param factory - The entity fixture factory.
 * @param count - How many entities to build.
 * @param overrides - Static overrides, or a callback returning overrides per index.
 * @returns {TModel[]} The built entities.
 */
export function createMany<TModel>(
    factory: FixtureFactory<TModel>,
    count: number,
    overrides?: Partial<TModel> | ((index: number) => Partial<TModel>),
): TModel[] {
    return range(count, (index: number) => {
        const partial: Partial<TModel> =
            typeof overrides === 'function'
                ? (overrides as (index: number) => Partial<TModel>)(index)
                : (overrides ?? {});

        return factory(partial, index);
    });
}

// -----------------------------------------------------------------------------------------------------
// @ BUILDER
// -----------------------------------------------------------------------------------------------------

/**
 * The base class every entity fixture builder extends.
 *
 * It holds a plain draft (never a model instance) so no deep clone is ever needed:
 * `build()` runs the entity constructor, which yields a fresh instance with an intact prototype.
 * Never use `structuredClone` on a domain model here — it strips the prototype and the getters
 * (`Property.displayName`, `Property.address`) disappear.
 */
export abstract class FixtureBuilder<TModel extends BaseModel> {
    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    protected draft: Partial<TModel>;

    // -----------------------------------------------------------------------------------------------------
    // @ CONSTRUCTOR
    // -----------------------------------------------------------------------------------------------------

    constructor(defaults: Partial<TModel>) {
        this.draft = { ...defaults };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Merges arbitrary fields into the draft — the escape hatch for one-off test data.
     *
     * @param partial - The fields to merge.
     * @returns {this} The builder, for chaining.
     */
    with(partial: Partial<TModel>): this {
        this.draft = { ...this.draft, ...partial };

        return this;
    }

    /**
     * Overrides the id of the entity under construction.
     *
     * @param id - The id to set.
     * @returns {this} The builder, for chaining.
     */
    withId(id: string): this {
        return this.with({ id } as Partial<TModel>);
    }

    /**
     * Marks the entity as soft-deleted.
     *
     * @param daysAgo - How many days ago the entity was deleted.
     * @returns {this} The builder, for chaining.
     */
    asDeleted(daysAgo: number = 3): this {
        return this.with(deletedFields(daysAgo) as Partial<TModel>);
    }

    /**
     * Builds `count` entities from the current draft, applying a per-index mutation.
     *
     * @param count - How many entities to build.
     * @param mutate - Optional per-index overrides, e.g. `(index) => ({ name: `Unit ${index}` })`.
     * @returns {TModel[]} The built entities.
     */
    buildMany(count: number, mutate?: (index: number) => Partial<TModel>): TModel[] {
        const snapshot: Partial<TModel> = { ...this.draft };

        return range(count, (index: number) => {
            this.draft = { ...snapshot, ...(mutate ? mutate(index) : {}) };

            return this.build();
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ ABSTRACT METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Builds a fresh, fully-typed model instance from the current draft.
     *
     * @returns {TModel} The built entity.
     */
    abstract build(): TModel;
}

// -----------------------------------------------------------------------------------------------------
// @ PRIVATE
// -----------------------------------------------------------------------------------------------------

/**
 * A tiny, stable string hash used to namespace the generated fixture ids.
 *
 * @param value - The value to hash.
 * @returns {number} A non-negative hash.
 */
function hashCode(value: string): number {
    let hash: number = 0;

    for (let index = 0; index < value.length; index++) {
        hash = (hash << 5) - hash + value.charCodeAt(index);
        hash |= 0;
    }

    return Math.abs(hash);
}
